import React from "react";
import { motion } from "framer-motion";
import { Trash2, Recycle, ShoppingBag, Users } from "lucide-react";

const data = [
  {
    id: "01",
    icon: Trash2,
    title: "Open Dumping",
    detail:
      "Waste dumped at empty plots, under bridges, and roadside corners creates persistent 'garbage vulnerable points' across the city.",
  },
  {
    id: "02",
    icon: Recycle,
    title: "No Segregation",
    detail:
      "Wet and dry waste mixed together makes composting and recycling impossible — half of Morbi's daily waste could be recovered.",
  },
  {
    id: "03",
    icon: ShoppingBag,
    title: "Single-Use Plastic",
    detail:
      "Thin plastic bags, wrappers, and bottles choke stormwater drains and litter public spaces across every ward.",
  },
  {
    id: "04",
    icon: Users,
    title: "Low Awareness",
    detail:
      "Many residents still rely on municipal sweepers alone — rather than adopting daily, responsible waste habits at source.",
  },
];

export const Challenges = () => {
  return (
    <section
      id="challenges"
      className="relative py-28 sm:py-36 bg-[color:var(--mati-forest)] text-[color:var(--mati-sand)] overflow-hidden"
      data-testid="challenges-section"
    >
      <div className="absolute inset-0 ceramic-grid-dark opacity-40" />
      <div
        className="absolute inset-x-0 top-0 h-[40vh] pointer-events-none"
        style={{
          background:
            "radial-gradient(800px 400px at 30% 0%, rgba(200,90,50,0.18) 0%, transparent 70%)",
        }}
      />

      <div className="relative max-w-[1360px] mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-12 gap-6 lg:gap-10 items-end mb-16">
          <div className="col-span-12 lg:col-span-6">
            <div className="eyebrow text-[color:var(--mati-clay)]">
              02 · The Roadblocks
            </div>
            <h2 className="font-display font-bold text-5xl sm:text-6xl lg:text-7xl mt-5 leading-[0.95] tracking-tight">
              Four truths we{" "}
              <span className="italic text-[color:var(--mati-clay)]">
                refuse
              </span>{" "}
              to ignore.
            </h2>
          </div>
          <p className="col-span-12 lg:col-span-5 lg:col-start-8 font-sub text-base sm:text-lg opacity-80 leading-relaxed">
            Before any solution, name the problem. These are the four
            structural challenges every Morbikar can see, every day — on their
            own street.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-[color:var(--mati-border-dark)]">
          {data.map((d, i) => {
            const Icon = d.icon;
            return (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.8,
                  ease: [0.16, 1, 0.3, 1],
                  delay: i * 0.08,
                }}
                className={`group relative p-8 sm:p-10 min-h-[320px] flex flex-col justify-between border-b md:border-b-0 border-[color:var(--mati-border-dark)] ${
                  i !== 3 ? "md:border-r" : ""
                } hover:bg-[color:var(--mati-forest-2)] transition-colors`}
                data-testid={`challenge-card-${d.id}`}
              >
                <div className="absolute left-6 top-6 font-mono-ui text-[10px] tracking-[0.3em] text-[color:var(--mati-clay)]">
                  / {d.id}
                </div>
                <div className="mt-16">
                  <Icon
                    className="w-10 h-10 text-[color:var(--mati-clay)] group-hover:scale-110 transition-transform origin-left"
                    strokeWidth={1.4}
                  />
                  <h3 className="font-display text-2xl sm:text-3xl font-bold mt-6">
                    {d.title}
                  </h3>
                </div>
                <p className="mt-6 font-sub text-sm opacity-75 leading-relaxed">
                  {d.detail}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Challenges;
